import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
  SheetFooter,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ChevronLeft,
  ChevronRight,
  Filter,
  Plus,
  Settings,
  Edit,
  Copy,
  // BarChart,
  Trash,
  X,
  Calendar,
  MessageSquare,
  Bell,
  Mail,
  AlertCircle,
  Search,
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { cn } from '@/lib/utils';
import { replaceRouteParam } from '@/utils/routes';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useEmailMarketing } from '@/hooks/useEmailMarketing';
import {
  EmailCampaignFilter,
  emailMarketingStore,
} from '@/state/EmailMarketingStore';
import { useList } from '@/hooks/useList';
import { notify } from '@/helpers';
import { format } from 'date-fns';
import routeNames from '@/routes/routeNames';
import { AuthStore } from '@/state/AuthenticationStore';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '@/components/atoms/modal/ConfirmationModal';
import { goToSetup } from '@/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { listStore } from '@/state/ListStore';
// import { listStore } from '@/state/ListStore';

interface CampaignDetails {
  goals: string[];
  title: string;
  templateId: string;
  listId: string;
  type: string;
  draftDate: Date;
  status: EmailCampaignFilter;
}

const CampaignCalendar = () => {
  const navigate = useNavigate();
  const [_, setSelectedDate] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentView] = useState<'month' | 'week'>('month');
  const [startHour, setStartHour] = useState(8);
  const weekViewRef = useRef<HTMLDivElement>(null);
  const [campaignType, setCampaignType] = useState('');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [displayErrorMsg] = useState<boolean>(false);
  const [campaignName, setCampaignName] = useState<string>(
    emailMarketingStore?.campaignName,
  );
  const [draftDate, setDraftDate] = useState<string>(
    format(new Date(), 'yyyy-MM-dd'),
  );
  const [goals, setGoals] = useState<string[]>([]);
  const [goal, setGoal] = useState('');
  const [channel, setChannel] = useState('');
  const [details, setDetails] = useState<CampaignDetails>({
    goals: [],
    title: '',
    templateId: '',
    listId: '',
    type: '',
    draftDate: new Date(),
    status: EmailCampaignFilter.DRAFT,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedList, setSelectedList] = useState<string | undefined>(
    undefined,
  );
  const [sendingOrder, setSendingOrder] = useState<string | undefined>(
    undefined,
  );
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<string | null>(
    null,
  );
  const [selectedTimePeriod, setSelectedTimePeriod] =
    useState<string>('Last_30_days');
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);

  const { getLists } = useList();
  const {
    createCampaign,
    getEmailCampaignStatusStats,
    updateCampaign,
    getCampaign,
    deleteCampaign,
    editCampaign,
    filterCampaigns,
    getEmailCampaignsByMonth,
    cloneEmailCampaign,
  } = useEmailMarketing();
  const { debounce } = useDebounce();

  const workspaceId =
    AuthStore.user_workspace_info?.active_workspace?.workspace?._id;
  const campaigns = emailMarketingStore?.campaignsByMonth || [];
  const filteredCampaigns = emailMarketingStore?.filteredCampaigns || [];
  const lists = listStore?.lists;

  const baseCampaigns =
    !searchTerm &&
    !selected &&
    !selectedList &&
    !sendingOrder &&
    !selectedTimeFrame &&
    !startDate &&
    !endDate
      ? campaigns
      : filteredCampaigns;

  const prevPeriod = () => {
    setCurrentDate(
      new Date(currentDate?.getFullYear(), currentDate?.getMonth() - 1, 1),
    );
  };

  const nextPeriod = () => {
    setCurrentDate(
      new Date(currentDate?.getFullYear(), currentDate?.getMonth() + 1, 1),
    );
  };

  const prevWeek = () => {
    setCurrentDate(
      new Date(
        currentDate?.getFullYear(),
        currentDate?.getMonth(),
        currentDate?.getDate() - 7,
      ),
    );
  };

  const nextWeek = () => {
    setCurrentDate(
      new Date(
        currentDate?.getFullYear(),
        currentDate?.getMonth(),
        currentDate?.getDate() + 7,
      ),
    );
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const next = () => {
    navigate(
      replaceRouteParam(
        `${routeNames.dashboard.emailMarketing}/campaign-flow/${channel}`,
        'workspaceId',
        AuthStore.user_workspace_info?.active_workspace?.workspace?._id,
      ),
    );
  };

  const handleChange = useCallback((e: any) => {
    const { name, value } = e.target;
    setDetails((prevDetails: any) => ({
      ...prevDetails,
      [name]: value.trimStart(),
    }));
  }, []);

  const handleUpdateClick = (selectedCampaign: string) => {
    getCampaign(selectedCampaign);
    setIsEditing(true);
  };

  const handleDateChange = (date: Date | undefined) => {
    if (!date) return;

    const dateString = format(date, 'yyyy-MM-dd');
    setDraftDate(dateString);
    setDetails(prev => ({
      ...prev,
      draftDate: new Date(dateString),
    }));
  };

  const handleDateClick = (day: number) => {
    const date = `${currentDate?.getFullYear()}-${String(
      currentDate?.getMonth() + 1,
    ).padStart(2, '0')}-${String(day)?.padStart(2, '0')}`;
    setSelectedDate(date);
    openModal(date);
  };

  const handleCampaignTypeChange = (type: string) => {
    setCampaignType(type);
    setChannel(type);
    setDetails(prevDetails => ({
      ...prevDetails,
      type,
    }));
  };

  const openModal = (date?: string) => {
    if (!emailMarketingStore.setupResponse?.domain) {
      notify('error', 'Add or verify a domain first');
      navigate(goToSetup(workspaceId));
    } else {
      const draftDateValue = date ? new Date(date) : new Date();
      setDetails(prevDetails => ({
        ...prevDetails,
        goals: [],
        title: '',
        templateId: '',
        listId: '',
        type: campaignType,
        draftDate: draftDateValue,
        status: EmailCampaignFilter?.DRAFT,
      }));
      setDraftDate(format(draftDateValue, 'yyyy-MM-dd'));
      setCampaignType(campaignType);
      setChannel(campaignType as string);
      setGoals([]);
      setIsOpenModal(true);
    }
  };

  const openTypeModal = (campaignType?: string) => {
    if (!emailMarketingStore.setupResponse?.domain) {
      notify('error', 'Add or verify a domain first');
      navigate(goToSetup(workspaceId));
    } else {
      const draftDateValue = draftDate ? new Date(draftDate) : new Date();
      setIsEditing(false);
      setDetails(prevDetails => ({
        ...prevDetails,
        goals: [],
        title: '',
        templateId: '',
        listId: '',
        type: campaignType ?? '',
        draftDate: draftDateValue,
        status: EmailCampaignFilter?.DRAFT,
      }));
      setCampaignType(campaignType as string);
      setChannel(campaignType as string);
      setDraftDate(format(draftDateValue, 'yyyy-MM-dd'));
      setGoals([]);
      setIsOpenModal(true);
    }
  };

  const handlSave = () => {
    createCampaign(details);
    setIsOpenModal(false);
  };

  const handleUpdate = () => {
    updateCampaign({
      ...details,
      id: emailMarketingStore?.campaign?._id as string,
    });
    setIsOpenModal(false);
  };

  const handleEdit = () => {
    if (emailMarketingStore?.campaign?._id as string) {
      editCampaign(emailMarketingStore?.campaign?._id as string, campaignName);
      setIsEditing(false);
    }
  };

  const handleKeyUp = (e: any) => {
    if (e.key === 'Enter' && goal?.trim() !== '') {
      const goalParts = goal
        .split(',')
        .map(g => g.trim())
        .filter(g => g !== '');
      setGoals(prevGoals => [...prevGoals, ...goalParts]);
      setDetails((prevDetails: any) => ({
        ...prevDetails,
        goals: [...prevDetails.goals, ...goalParts],
      }));
      setGoal('');
    }
  };

  const handleGoalChange = (e: any) => {
    setGoal(e.target.value);
  };

  const handleRemove = (goalToRemove: string) => {
    setGoals(prevGoals => prevGoals?.filter(g => g !== goalToRemove));
    setDetails((prevDetails: any) => ({
      ...prevDetails,
      goals: prevDetails?.goals?.filter((g: any) => g !== goalToRemove),
    }));
  };

  const handleSubmit = () => {
    if (
      !details?.title ||
      details?.goals?.length === 0 ||
      !details.listId ||
      !details?.templateId
    ) {
      notify('error', 'Please fill all the details');
      return;
    }
    if (isEditing && emailMarketingStore?.campaign?._id) {
      handleUpdate();
    } else {
      handlSave();
    }
  };

  const handleCheckboxChange = (id: string) => {
    setSelected(id === selected ? null : id);
  };

  const handleCheckboxTimeChange = (id: string) => {
    setSelectedTimeFrame(id === selectedTimeFrame ? null : id);
  };

  const handleFilterCampaign = () => {
    filterCampaigns({
      title: searchTerm,
      status: selected as EmailCampaignFilter | undefined,
      listId: selectedList,
      dateRange: selectedTimeFrame ?? '',
      sendingOrder: sendingOrder,
      customStart: startDate,
      customEnd: endDate,
    });
    setIsFilterOpen(false);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelected(null);
    setSelectedList(undefined);
    setSendingOrder(undefined);
    setSelectedTimeFrame(null);
    setStartDate(undefined);
    setEndDate(undefined);
    filterCampaigns({});
    setIsFilterOpen(false);
  };

  useEffect(() => {
    debounce(
      () =>
        filterCampaigns({ title: searchTerm, dateRange: selectedTimePeriod }),
      500,
    );
  }, [searchTerm]);

  useEffect(() => {
    getLists();
  }, []);

  useEffect(() => {
    emailMarketingStore.setCampaignName(details?.title);
    let draftDate = details?.draftDate ? details?.draftDate : '';

    if (draftDate && typeof draftDate !== 'string') {
      draftDate = draftDate?.toISOString();
    }

    emailMarketingStore.setDraftDate(draftDate);
  }, [details]);

  useEffect(() => {
    if (isEditing && emailMarketingStore?.campaign) {
      const campaignData = emailMarketingStore?.campaign;
      setDetails({
        goals: campaignData?.goals || [],
        title: campaignData?.title || '',
        templateId: campaignData?.template || '',
        listId: campaignData?.listId || '',
        status: campaignData?.status || '',
        type: campaignData?.type || '',
        draftDate: campaignData?.draftDate || '',
      });
      setCampaignType(campaignData?.type || '');
      setDraftDate(
        campaignData?.draftDate
          ? format(new Date(campaignData?.draftDate), 'yyyy-MM-dd')
          : '',
      );

      setGoals(campaignData?.goals || []);
    }
  }, [emailMarketingStore?.campaign, isEditing]);

  useEffect(() => {
    getEmailCampaignStatusStats();
  }, [EmailCampaignFilter?.ALL]);

  useEffect(() => {
    const year = currentDate?.getFullYear()?.toString();
    const month = (currentDate?.getMonth() + 1)?.toString()?.padStart(2, '0');
    getEmailCampaignsByMonth({ year, month });
  }, [currentDate]);

  const renderMonthCampaigns = (day: number) => {
    const dateStr = `${currentDate.getFullYear()}-${String(
      currentDate?.getMonth() + 1,
    )?.padStart(2, '0')}-${String(day)?.padStart(2, '0')}`;

    const dayCampaigns = baseCampaigns?.filter(
      campaign =>
        new Date(campaign?.draftDate)?.toISOString()?.split('T')[0] === dateStr,
    );

    const isPastDate = new Date(dateStr) < new Date(new Date().toDateString());

    if (emailMarketingStore?.campaignMonthLoader) {
      return (
        <div className='space-y-1.5'>
          <Skeleton className='h-2 w-[40%]' />
          <Skeleton className='h-2 w-[60%]' />
          <Skeleton className='h-2 w-[75%]' />
          <Skeleton className='h-2 w-[80%]' />
          <Skeleton className='h-2 w-[95%]' />
          <Skeleton className='h-2 w-[98%]' />
        </div>
      );
    }

    return (
      <>
        {dayCampaigns?.map(campaign => (
          <div
            key={campaign?._id}
            className='text-xs p-1 bg-secondary/20 rounded mb-1 truncate cursor-pointer 
            hover:bg-secondary/30 transition-colors duration-200 group relative'
          >
            {campaign?.title}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-4 w-4 absolute right-1 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200'
                >
                  <Settings className='h-3 w-3' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onClick={() => {
                    handleUpdateClick(campaign?._id);
                    setCampaignName(campaign?.title);
                  }}
                >
                  <Edit className='mr-2 h-4 w-4' />
                  Edit
                </DropdownMenuItem>
                {campaign.status !== EmailCampaignFilter.SCHEDULED && (
                  <DropdownMenuItem
                    onClick={e => {
                      e.stopPropagation();
                      cloneEmailCampaign({
                        id: campaign?._id,
                        sendAt: new Date(),
                      });
                    }}
                  >
                    <Copy className='mr-2 h-4 w-4' />
                    Clone
                  </DropdownMenuItem>
                )}
                {/* <DropdownMenuItem>
                  <BarChart className='mr-2 h-4 w-4' />
                  Analytics
                </DropdownMenuItem> */}
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation();
                    emailMarketingStore.setCampaign(campaign);
                    setIsOpenConfirmationModal(true);
                  }}
                >
                  <Trash className='mr-2 h-4 w-4' />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}

        {!isPastDate ? (
          <div
            className='text-xs p-1 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors duration-200'
            onClick={() => handleDateClick(day)}
          >
            + New Campaign
          </div>
        ) : (
          <div className='text-xs p-1 text-gray-400 transition-colors duration-200'>
            + New Campaign
          </div>
        )}
      </>
    );
  };

  const renderWeekView = () => {
    const weekStart = new Date(currentDate);
    weekStart?.setDate(currentDate?.getDate() - currentDate?.getDay());
    const weekDays = Array?.from({ length: 7 }, (_, i) => {
      const day = new Date(weekStart);
      day?.setDate(weekStart?.getDate() + i);
      return day;
    });

    const timeSlots = Array?.from({ length: 24 }, (_, i) => {
      const hour = (startHour + i) % 24;
      return `${hour % 12 || 12}:00 ${hour < 12 ? 'AM' : 'PM'}`;
    });

    return (
      <div className='grid grid-cols-8 gap-x-2' ref={weekViewRef}>
        <div className='col-span-1'></div>
        {weekDays?.map((day, index) => (
          <div key={index} className='text-center font-semibold p-2 relative'>
            {index === 0 && (
              <Button
                variant='ghost'
                size='icon'
                className='absolute left-0 top-1/2 transform -translate-y-1/2'
                onClick={prevWeek}
              >
                <ChevronLeft className='h-4 w-4' />
              </Button>
            )}
            {day?.toLocaleDateString('en-US', { weekday: 'short' })}
            <br />
            {day?.getDate()}
            {index === 6 && (
              <Button
                variant='ghost'
                size='icon'
                className='absolute right-0 top-1/2 transform -translate-y-1/2'
                onClick={nextWeek}
              >
                <ChevronRight className='h-4 w-4' />
              </Button>
            )}
          </div>
        ))}
        {timeSlots?.map((time, timeIndex) => (
          <React.Fragment key={time}>
            <div className='text-right pr-2 text-sm'>{time}</div>
            {weekDays?.map((day, dayIndex) => {
              const date = `${day?.getFullYear()}-${String(
                day?.getMonth() + 1,
              )?.padStart(2, '0')}-${String(day?.getDate())?.padStart(2, '0')}`;
              const dayCampaigns = baseCampaigns?.filter(
                campaign =>
                  new Date(campaign?.draftDate)
                    ?.toISOString()
                    ?.split('T')[0] === date &&
                  parseInt(
                    new Date(campaign?.draftDate)
                      ?.toISOString()
                      ?.split('T')[1]
                      .slice(0, 5)
                      .split(':')[0],
                  ) ===
                    (startHour + timeIndex) % 24,
              );
              return (
                <div
                  key={`${dayIndex}-${timeIndex}`}
                  className='border-t border-l border-r p-1 h-12 overflow-hidden'
                >
                  {dayCampaigns?.map(campaign => (
                    <div
                      key={campaign?._id}
                      className='text-xs p-1 bg-secondary/20 rounded mb-1 truncate cursor-pointer hover:bg-secondary/30 transition-colors duration-200'
                      onClick={() => handleUpdateClick(campaign?._id)}
                    >
                      {campaign?.title}
                    </div>
                  ))}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    );
  };

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (currentView === 'week') {
        e.preventDefault();
        setStartHour(prevStartHour => {
          const newStartHour = prevStartHour + (e.deltaY > 0 ? 1 : -1);
          return Math.max(0, Math.min(23, newStartHour));
        });
      }
    };

    const weekViewElement = weekViewRef.current;
    if (weekViewElement) {
      weekViewElement.addEventListener('wheel', handleWheel, {
        passive: false,
      });
    }

    return () => {
      if (weekViewElement) {
        weekViewElement.removeEventListener('wheel', handleWheel);
      }
    };
  }, [currentView]);

  return (
    <div className='bg-white p-6 rounded-lg shadow-sm max-w-full mx-auto mb-20 mt-10'>
      <h1 className='text-2xl font-bold mb-4'>Campaign Calendar</h1>

      {/* Search and Filter Controls */}
      <div className='flex flex-col space-y-4 mb-6'>
        <div className='relative flex-grow'>
          <Search className='absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400' />
          <Input
            type='text'
            placeholder='Search campaigns...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className='pl-10 pr-4 py-2 w-full focus-visible:outline-none focus-visible:ring-0
           focus-visible:ring-ring focus-visible:ring-offset-0 focus-within:border focus-within:border-secondary'
          />
        </div>
        <div className='flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-4'>
          <Select
            onValueChange={value => {
              setSelectedTimePeriod(value);
              filterCampaigns({ dateRange: value });
            }}
          >
            <SelectTrigger className='w-full md:w-[200px] focus:ring-0 focus:ring-offset-0 focus-within:border focus-within:border-secondary'>
              {selectedTimePeriod?.replace(/_/g, ' ')}
            </SelectTrigger>
            <SelectContent>
              {['Last_30_days', 'Last_60_days', 'Last_90_days']?.map(id => (
                <SelectItem key={id} value={id}>
                  {id?.replace(/_/g, ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <SheetTrigger asChild>
              <Button variant='outline' className='w-full md:w-[120px]'>
                <Filter className='w-4 h-4 mr-2' />
                Filters
              </Button>
            </SheetTrigger>
            <SheetContent className='w-[400px] max-h-screen flex flex-col'>
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className='flex-grow py-4 pr-4 overflow-y-auto overflow-x-hidden'>
                <h3 className='mt-4 mb-2 font-semibold'>Status</h3>
                <div className='space-y-2'>
                  {['Sent', 'Scheduled', 'Draft']?.map(id => (
                    <div key={id} className='flex items-center'>
                      <Checkbox
                        id={id}
                        checked={selected === id}
                        onCheckedChange={() => handleCheckboxChange(id)}
                        className='flex-none'
                      />
                      <label htmlFor={id} className='ml-2 capitalize'>
                        {id?.replace('_', ' ')}
                      </label>
                    </div>
                  ))}
                </div>
                {/* <h3 className='mt-4 mb-2 font-semibold'>Sending order</h3>
                <Select
                  value={sendingOrder}
                  onValueChange={value => setSendingOrder(value)}
                >
                  <SelectTrigger
                    className='w-full focus:ring-0 focus:ring-offset-0
                   focus-within:border focus-within:border-secondary'
                  >
                    <SelectValue placeholder='Select order' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='Ascending'>Ascending</SelectItem>
                    <SelectItem value='Descending'>Descending</SelectItem>
                  </SelectContent>
                </Select> */}
                <h3 className='mt-4 mb-2 font-semibold'>List</h3>
                <Select
                  value={selectedList}
                  onValueChange={value => {
                    setSelectedList(value);
                  }}
                >
                  <SelectTrigger
                    className='w-full focus:ring-0 focus:ring-offset-0
                   focus-within:border focus-within:border-secondary'
                  >
                    <SelectValue placeholder='Select one or more options' />
                  </SelectTrigger>
                  <SelectContent className='w-full h-full'>
                    {lists?.map(list => (
                      <SelectItem key={list?._id} value={list?._id}>
                        {list?.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <h3 className='mt-4 mb-2 font-semibold'>Date Sent</h3>
                <div className='space-y-2'>
                  {[
                    'Last_7_days',
                    'Last_30_days',
                    'Last_60_days',
                    'Custom',
                  ]?.map(id => (
                    <div key={id} className='flex items-center'>
                      <Checkbox
                        id={id}
                        checked={selectedTimeFrame === id}
                        onCheckedChange={() => handleCheckboxTimeChange(id)}
                      />
                      <label htmlFor={id} className='ml-2 capitalize'>
                        {id?.replace(/_/g, ' ')}
                      </label>
                    </div>
                  ))}
                </div>
                {selectedTimeFrame == 'Custom' && (
                  <div className='mt-2 space-y-2'>
                    <Input
                      type='date'
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      placeholder='Start date'
                    />
                    <Input
                      type='date'
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      placeholder='End date'
                    />
                  </div>
                )}
              </div>
              <SheetFooter>
                <div className='flex justify-between items-center w-full'>
                  <Button variant='outline' onClick={clearFilters}>
                    Clear
                  </Button>
                  <SheetClose>
                    <Button
                      onClick={handleFilterCampaign}
                      disabled={
                        !selected &&
                        !selectedList &&
                        !sendingOrder &&
                        !selectedTimeFrame &&
                        !startDate &&
                        !endDate
                      }
                      className='w-full bg-black text-white'
                    >
                      Apply Filters
                    </Button>
                  </SheetClose>
                </div>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Calendar Controls */}
      <div className='flex justify-between items-center mb-4'>
        <div className='flex items-center space-x-4'>
          <Button
            onClick={goToToday}
            className='bg-secondary text-white hover:bg-secondary/80'
          >
            Today
          </Button>
          <Button
            variant='outline'
            onClick={currentView === 'month' ? prevPeriod : prevWeek}
          >
            <ChevronLeft className='h-4 w-4' />
          </Button>
          <Button
            variant='outline'
            onClick={currentView === 'month' ? nextPeriod : nextWeek}
          >
            <ChevronRight className='h-4 w-4' />
          </Button>
          <span className='text-lg font-semibold'>
            {currentDate?.toLocaleString('default', {
              month: 'long',
              year: 'numeric',
            })}
          </span>
        </div>
        <div className='flex items-center space-x-4'>
          {/* <Select
            value={currentView}
            onValueChange={value => setCurrentView(value as 'month' | 'week')}
          >
            <SelectTrigger className='w-[180px] focus:ring-0 focus:ring-offset-0 focus-within:border focus-within:border-secondary'>
              <SelectValue placeholder='Select view' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='week'>Week</SelectItem>
              <SelectItem value='month'>Month</SelectItem>
            </SelectContent>
          </Select> */}
          <DropdownMenu
            onOpenChange={() => setDraftDate(format(new Date(), 'yyyy-MM-dd'))}
          >
            <DropdownMenuTrigger asChild>
              <Button className='bg-secondary text-white hover:bg-secondary/80'>
                <Plus className='h-4 w-4 mr-2' />
                New Campaign
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                className='text-md'
                onSelect={() => {
                  setDetails({ ...details });
                  openTypeModal('EMAIL');
                }}
              >
                Email Campaign
              </DropdownMenuItem>
              <DropdownMenuItem
                className='text-md'
                onSelect={() => {
                  setDetails({ ...details });
                  openTypeModal('SMS');
                }}
                disabled
              >
                SMS Campaign
              </DropdownMenuItem>
              <DropdownMenuItem
                className='text-md'
                onSelect={() => {
                  setDetails({ ...details });
                  openTypeModal('PUSH');
                }}
                disabled
              >
                Push Campaign
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Calendar View */}
      {currentView === 'month' ? (
        <div className='grid grid-cols-7 gap-2'>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map(day => (
            <div key={day} className='text-center font-semibold p-2'>
              {day}
            </div>
          ))}
          {Array?.from({
            length: new Date(
              currentDate?.getFullYear(),
              currentDate?.getMonth() + 1,
              0,
            )?.getDate(),
          })?.map((_, index) => {
            const day = index + 1;
            const dateStr = `${currentDate?.getFullYear()}-${String(
              currentDate?.getMonth() + 1,
            )?.padStart(2, '0')}-${String(day)?.padStart(2, '0')}`;
            const isPastDate =
              new Date(dateStr) < new Date(new Date()?.toDateString());

            return (
              <div
                key={index}
                className={`${
                  isPastDate
                    ? 'bg-gray-100/50 '
                    : 'cursor-pointer hover:bg-gray-50/50 '
                } p-2 border rounded-md h-32 overflow-y-auto relative transition-colors duration-200 `}
              >
                <div className='font-semibold mb-1'>{day}</div>
                {renderMonthCampaigns(day)}
              </div>
            );
          })}
        </div>
      ) : (
        renderWeekView()
      )}

      {/* Create/Edit Campaign Modal */}
      <Dialog open={isOpenModal} onOpenChange={setIsOpenModal}>
        <DialogContent className='bg-white border-none p-8 w-full max-w-xl max-h-[90vh] overflow-y-auto [&>button]:hidden'>
          <DialogHeader className='flex flex-row justify-between items-center'>
            <DialogTitle className='text-2xl font-semibold text-secondary'>
              {isEditing ? 'Edit' : 'Create'}
            </DialogTitle>
            <DialogClose asChild>
              <Button variant='ghost' size='icon'>
                <X className='h-6 w-6' />
              </Button>
            </DialogClose>
          </DialogHeader>
          <form onSubmit={handleSubmit} className='grid grid-cols-1 gap-4'>
            <div>
              <Label htmlFor='campaignName'>Campaign Name</Label>
              <Input
                id='campaignName'
                name='title'
                placeholder="Enter campaign name (e.g., 'Holiday Promo 2024')"
                value={details?.title}
                onChange={handleChange}
                className='focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring focus-visible:ring-offset-0 focus-within:border focus-within:border-secondary'
                required
              />
            </div>
            <div>
              <Label htmlFor='draftDate'>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !draftDate && 'text-muted-foreground',
                    )}
                  >
                    <Calendar className='mr-2 h-4 w-4' />
                    {draftDate
                      ? format(draftDate, 'd MMMM yyyy')
                      : 'Select a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-auto p-0'>
                  <CalendarComponent
                    mode='single'
                    selected={new Date(draftDate)}
                    onSelect={date => handleDateChange(date)}
                    initialFocus
                    fromDate={new Date()}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className='space-y-2'>
              <Label>Type</Label>
              <RadioGroup
                onValueChange={value => handleCampaignTypeChange(value)}
                value={campaignType}
                className='space-y-2'
              >
                {['EMAIL', 'SMS', 'PUSH']?.map(type => {
                  const isDisabled = type === 'PUSH' || type === 'SMS';

                  return (
                    <div
                      key={type}
                      className={`flex items-center space-x-2 rounded-md border p-4 transition-colors ${
                        campaignType === type
                          ? '!bg-secondary text-white'
                          : 'hover:bg-secondary/10'
                      } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <RadioGroupItem
                        value={type}
                        id={type}
                        disabled={isDisabled}
                      />
                      <Label
                        htmlFor={type}
                        className='flex items-center cursor-pointer'
                      >
                        {type === 'EMAIL' && <Mail className='mr-2 h-4 w-4' />}
                        {type === 'SMS' && (
                          <MessageSquare className='mr-2 h-4 w-4' />
                        )}
                        {type === 'PUSH' && <Bell className='mr-2 h-4 w-4' />}
                        {type?.charAt(0)?.toUpperCase() + type?.slice(1)}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
              <div>
                <Label htmlFor='goals'>Tags</Label>
                <Input
                  id='goals'
                  name='goals'
                  value={goal}
                  onChange={handleGoalChange}
                  onKeyUp={handleKeyUp}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                    }
                  }}
                  placeholder='Select or add tags (e.g., Holiday, Promotion)'
                  className='focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring focus-visible:ring-offset-0 focus-within:border focus-within:border-secondary'
                />
                <div className='flex flex-wrap gap-2 mt-2'>
                  {goals?.map((tag, index) => (
                    <span
                      key={index}
                      className='bg-primary/10 text-black px-2 py-1 rounded text-md'
                    >
                      {tag}
                      <button
                        type='button'
                        className='ml-2 text-primary'
                        onClick={() => handleRemove(tag)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <p className='text-md text-muted-foreground mt-1'>
                Push messages will go out from your Sticky Card.
              </p>
            </div>

            <div className='flex justify-end space-x-2 mt-4'>
              <Button
                type='button'
                className='bg-secondary text-white hover:bg-secondary/80'
                onClick={next}
                disabled={!details?.title || !campaignType}
              >
                Continue
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* EDIT */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className='bg-white flex flex-col border-none p-8 max-w-[600px] max-h-[90vh] overflow-y-auto [&>button]:hidden'>
          <DialogHeader className='flex flex-row justify-between items-center'>
            <DialogTitle className='text-2xl font-semibold text-secondary'>
              Rename Campaign
            </DialogTitle>
            <DialogClose asChild>
              <Button variant='ghost' size='icon'>
                <X className='h-6 w-6' />
              </Button>
            </DialogClose>
          </DialogHeader>
          <div className='w-full h-full flex flex-row'>
            <div className='w-full'>
              <Label>Campaign Name</Label>
              <Input
                className={`${displayErrorMsg ? 'border border-red-700' : ''}`}
                value={campaignName}
                onChange={e => {
                  setCampaignName(e.target.value);
                }}
              />
              {displayErrorMsg && (
                <div className='flex mt-1 flex-row'>
                  <AlertCircle className=' h-4 w-4 mr-1 text-red-700' />{' '}
                  <span className='text-xs text-red-700'>
                    This field is required.
                  </span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className='flex flex-row-reverse'>
            <Button variant={'outline'} onClick={() => setIsEditing(false)}>
              <p>Cancel</p>
            </Button>
            <Button
              className='bg-secondary text-white hover:bg-secondary/80'
              variant={'default'}
              onClick={handleEdit}
            >
              <p className='text-white'>Change name</p>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmationModal
        title='Delete Campaign'
        content='Are you sure you want to delete this campaign?'
        show={isOpenConfirmationModal}
        setShow={setIsOpenConfirmationModal}
        confirmText='Done'
        onConfirm={() => {
          deleteCampaign(emailMarketingStore?.campaign?._id as string);
          setIsOpenConfirmationModal(false);
        }}
      />
    </div>
  );
};

export default observer(CampaignCalendar);
